import { Button, Icon } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import { useRouterApplicationId } from 'components/Signup/hooks/useRouterApplicationId';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import usePhoneNumber from 'hooks/usePhoneNumber';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { TSignupErrorType } from 'types';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './OffersError.module.css';

type TProps = {
  error: TSignupErrorType;
};

export const OffersError = ({ error }: TProps) => {
  const applicationId = useRouterApplicationId();
  const { trackEvent } = useAmplitude();
  const { introducerType } = useIntroducerContext();
  const isAccountant = introducerType === 'accountant';
  const { phoneNumberLink } = usePhoneNumber(isAccountant);

  const introducerEmail = isAccountant
    ? t`accountants@iwoca.co.uk`
    : t`brokers@iwoca.co.uk`;

  const handleBackButtonTracking = () => {
    trackEvent(AMPLITUDE_EVENTS.backCTAClicked, {
      'CTA text': 'Back to the form',
      'Error message': _.capitalize(_.startCase(error)),
    });
  };

  const getErrorData = () => {
    switch (error) {
      // TODO: remove below 2 cases when we remove checkDetails cookie logic
      case 'credit_check':
        return {
          heading: t`We couldn’t run a soft credit check`,
          text: (
            <p className={styles.text}>
              <Trans>
                Please confirm the applicant's name, last 6 years address
                history and date of birth are accurate and try again.
              </Trans>
            </p>
          ),
        };
      case 'confirm_details':
        return {
          heading: t`We couldn't confirm the applicant details`,
          text: (
            <p className={styles.text}>
              <Trans>
                Please check that the main applicant is a director of the
                company.
              </Trans>
            </p>
          ),
        };
      case 'duplicate_guarantor':
        return {
          heading: t`It looks like one of the guarantors has been entered twice`,
          text: (
            <p className={styles.text}>
              <Trans>
                Please check that each guarantor is unique and try again.
              </Trans>
            </p>
          ),
        };
      case 'technical':
        return {
          heading: t`We have experienced a technical error`,
          text: (
            <p className={styles.text}>
              <Trans>
                If you continue to see this error, please{' '}
                <a
                  href={`mailto:${introducerEmail}`}
                  data-testid="email-link"
                  onClick={() => {
                    trackEvent(AMPLITUDE_EVENTS.contactLinkClicked, {
                      'CTA location': 'Signup form error modal',
                      'CTA destination': 'Email us',
                      'CTA text': 'Email us',
                      'CTA URL': `mailto:${introducerEmail}`,
                    });
                  }}
                >
                  email
                </a>{' '}
                or{' '}
                <a
                  href={`tel:${phoneNumberLink}`}
                  data-testid="call-link"
                  onClick={() => {
                    trackEvent(AMPLITUDE_EVENTS.contactLinkClicked, {
                      'CTA location': 'Signup form error modal',
                      'CTA destination': 'Call us',
                      'CTA text': 'Call us',
                      'CTA URL': `tel:${phoneNumberLink}`,
                    });
                  }}
                >
                  call
                </a>{' '}
                our introducer team.
              </Trans>
            </p>
          ),
        };
    }
  };

  const content = getErrorData();

  return (
    <div className={styles.errorContainer}>
      <h2 className={styles.heading}>{content.heading}</h2>
      {content.text}
      <Link to={PATH_NAMES.newApplicationStep1({ applicationId })}>
        <Button href="#" onClick={handleBackButtonTracking}>
          <Icon className={styles.icon} icon="arrowLeft" />
          <Trans>Back to the form</Trans>
        </Button>
      </Link>
    </div>
  );
};
